import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLayoutData } from '../../contexts/LayoutDataContext';
import { useServices } from '../../contexts/ServicesContext';
import { useNews } from '../../contexts/NewsContext';

export default function Header() {
  const layoutData = useLayoutData();
  const { setServicesData } = useServices();
  const { setFeaturedNews } = useNews();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState<string | null>(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMegaMenuOpen(null);
    setMobileDropdownOpen(null);
  }, [location]);

  // Đồng bộ từ LayoutDataContext sang ServicesContext, NewsContext (cho HomePage, SolutionsSection...)
  useEffect(() => {
    if (!layoutData.loading) {
      setServicesData(layoutData.services, layoutData.solutions, layoutData.academy);
      setFeaturedNews(layoutData.featuredNews);
    }
  }, [layoutData.loading, layoutData.services, layoutData.solutions, layoutData.academy, layoutData.featuredNews, setServicesData, setFeaturedNews]);

  const navLinks = [
    { path: '/', label: 'Trang Chủ' },
    { path: '/giai-phap-va-dich-vu', label: 'Giải Pháp', hasMegaMenu: true, megaMenuType: 'solutions' },
    { path: '/tin-tuc', label: 'Tin Tức', hasMegaMenu: true, megaMenuContent: layoutData.newsCategories, megaMenuType: 'news' },
    // { path: '/du-an', label: 'Dự Án' },
    { path: '/ve-chung-toi', label: 'Về Chúng Tôi' },
    { path: '/lien-he', label: 'Liên Hệ' }
  ];

  // Trang có hero nền sáng → chỉ đổi màu chữ & logo sang đen, nền vẫn transparent khi chưa scroll
  const isLightHeroPage = location.pathname === '/dich-vu/tu-van-chuyen-doi-so';
  const useDarkText = scrolled || isLightHeroPage;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-md" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              {/* Logo luôn hiển thị màu gốc */}
              <img
                src={`${
                  typeof window !== "undefined" ? window.location.origin : ""
                }/stackway/logo/logo.png`}
                alt="StackWay Solution"
                className="h-12 w-auto"
              />
              <span
                className={`text-2xl font-bold transition-colors ${
                  useDarkText ? "text-[#2a4b7c]" : "text-white"
                }`}
              >
                STACKWAY
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={() =>
                    link.hasMegaMenu && setMegaMenuOpen(link.label)
                  }
                  onMouseLeave={() => setMegaMenuOpen(null)}
                >
                  <Link
                    to={link.path}
                    className={`text-base font-semibold transition-colors flex items-center gap-1 ${
                      location.pathname === link.path
                        ? "text-[#00c897]"
                        : useDarkText
                        ? "text-[#333] hover:text-[#00c897]"
                        : "text-white hover:text-[#00c897]"
                    }`}
                  >
                    {link.label}
                  </Link>

                  {/* Mega Menu for Services */}
                  {link.hasMegaMenu &&
                    link.megaMenuType === "solutions" &&
                    megaMenuOpen === link.label && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-screen max-w-5xl">
                        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-slide-up">
                          <div className="grid grid-cols-12 gap-0">
                            {/* Left Column - Giải pháp (33%) */}
                            <div className="col-span-4 bg-gradient-to-br from-gray-50 to-white p-8 border-r border-gray-100">
                              <div className="mb-6">
                                <h3 className="text-xs font-bold text-[#2a4b7c] uppercase tracking-wider mb-2">
                                  GIẢI PHÁP
                                </h3>
                                <div className="w-12 h-1 bg-[#2a4b7c] rounded-full"></div>
                              </div>

                              <div className="space-y-3">
                                {layoutData.solutions.length > 0 ? (
                                  layoutData.solutions.map((solution) => (
                                    <Link
                                      key={solution._id}
                                      to={`/giai-phap/${solution.slug}`}
                                      className="group block p-3 rounded-lg hover:bg-white transition-all duration-300 hover:shadow-md"
                                    >
                                      <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] rounded-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0 shadow-lg">
                                          <i className={`${solution.icon || 'ri-service-line'} text-lg text-white`}></i>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h4 className="text-sm font-semibold text-[#2a4b7c] mb-1 group-hover:text-[#00c897] transition-colors">
                                            {solution.name}
                                          </h4>
                                          {solution.shortDescription && (
                                            <p className="text-xs text-[#4b5563] leading-relaxed line-clamp-3 mt-1 text-justify">
                                              {solution.shortDescription}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </Link>
                                  ))
                                ) : (
                                  <p className="text-sm text-gray-500 text-center py-4">
                                    Đang cập nhật...
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Middle Column - Dịch vụ (42%) */}
                            <div className="col-span-5 p-8">
                              <div className="mb-6">
                                <h3 className="text-xs font-bold text-[#2a4b7c] uppercase tracking-wider mb-2">
                                  DỊCH VỤ
                                </h3>
                                <div className="w-12 h-1 bg-[#2a4b7c] rounded-full"></div>
                              </div>

                              <div className="space-y-3">
                                {layoutData.services.length > 0 ? (
                                  layoutData.services.map((service) => (
                                    <Link
                                      key={service._id}
                                      to={`/dich-vu/${service.slug}`}
                                      className="group flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all duration-300"
                                    >
                                      <div className="w-10 h-10 flex items-center justify-center bg-[#2a4b7c]/10 rounded-lg group-hover:bg-[#00c897]/20 transition-colors flex-shrink-0">
                                        <i className={`${service.icon || 'ri-service-line'} text-lg text-[#2a4b7c] group-hover:text-[#00c897] transition-colors`}></i>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-semibold text-[#2a4b7c] group-hover:text-[#00c897] transition-colors">
                                          {service.name}
                                        </h4>
                                        {service.shortDescription && (
                                          <p className="text-xs text-[#4b5563] line-clamp-3 mt-1 text-justify">
                                            {service.shortDescription}
                                          </p>
                                        )}
                                      </div>
                                    </Link>
                                  ))
                                ) : (
                                  <p className="text-sm text-gray-500 text-center py-4">
                                    Đang cập nhật...
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Right Column - Academy (25%) */}
                            {layoutData.academy ? (
                              <div className="col-span-3 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] p-8 relative overflow-hidden">
                                {/* Decorative elements */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-[#00c897]/10 rounded-full blur-3xl"></div>
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#00c897]/10 rounded-full blur-2xl"></div>

                                <div className="relative z-10">
                                  <div className="w-14 h-14 flex items-center justify-center bg-[#00c897] rounded-xl mb-4 shadow-lg">
                                    <i className={`${layoutData.academy.icon || 'ri-graduation-cap-line'} text-2xl text-white`}></i>
                                  </div>

                                  <h3 className="text-lg font-bold text-white mb-2">
                                    {layoutData.academy.name}
                                  </h3>

                                  <p className="text-sm text-gray-300 leading-relaxed mb-6 text-justify">
                                    {layoutData.academy.shortDescription}
                                  </p>

                                  <Link
                                    to={`/academy/${layoutData.academy.slug}`}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00c897] text-white text-sm font-semibold rounded-lg hover:bg-[#00b587] transition-all duration-300 whitespace-nowrap shadow-lg hover:shadow-xl hover:scale-105"
                                  >
                                    Khám phá ngay
                                    <i className="ri-arrow-right-line"></i>
                                  </Link>
                                </div>
                              </div>
                            ) : (
                              <div className="col-span-3 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] p-8 relative overflow-hidden">
                                <div className="relative z-10 flex items-center justify-center h-full">
                                  <p className="text-sm text-gray-300 text-center">
                                    Đang cập nhật...
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Mega Menu for News */}
                  {link.hasMegaMenu &&
                    link.megaMenuType === "news" &&
                    megaMenuOpen === link.label && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-screen max-w-5xl">
                        <div className="bg-white rounded-xl shadow-2xl p-8 border border-gray-100 animate-slide-up">
                          <div className="grid grid-cols-12 gap-0">
                            {/* Left: Categories */}
                            <div className="col-span-5 p-8 bg-gray-50 border-r border-gray-200">
                              <h3 className="text-lg font-bold text-[#333] mb-6 flex items-center gap-2">
                                <i className="ri-folder-line text-[#00c897]"></i>
                                Danh Mục Tin Tức
                              </h3>
                              <div className="space-y-3">
                                {layoutData.newsCategories.map((category) => (
                                  <Link
                                    key={category._id}
                                    to={`/tin-tuc-${category.slug}`}
                                    className="group/item flex items-start gap-3 p-3 rounded-lg hover:bg-white transition-all duration-300"
                                  >
                                    <div className="w-10 h-10 flex items-center justify-center bg-[#2a4b7c] rounded-lg group-hover/item:bg-[#00c897] transition-colors flex-shrink-0">
                                      <i
                                        className={`${
                                          category.icon || "ri-folder-line"
                                        } text-lg text-white`}
                                      ></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-semibold text-[#333] group-hover/item:text-[#00c897] transition-colors text-sm mb-1">
                                        {category.name}
                                      </div>
                                      <div className="text-xs text-gray-600 line-clamp-1">
                                        {category.description || ""}
                                      </div>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                              <Link
                                to="/tin-tuc"
                                className="mt-6 flex items-center justify-center gap-2 px-4 py-3 bg-[#00c897] text-white text-sm font-semibold rounded-lg hover:bg-[#00b587] transition-colors whitespace-nowrap"
                              >
                                Xem tất cả tin tức
                                <i className="ri-arrow-right-line"></i>
                              </Link>
                            </div>

                            {/* Right: Featured News */}
                            <div className="col-span-7 p-8">
                              <h3 className="text-lg font-bold text-[#333] mb-6 flex items-center gap-2">
                                <i className="ri-fire-line text-[#00c897]"></i>
                                Tin Nổi Bật
                              </h3>
                              <div className="space-y-4">
                                {layoutData.featuredNews.map((news) => (
                                  <Link
                                    key={news._id}
                                    to={`/tin-tuc/${news.slug}`}
                                    className="group/news flex gap-4 p-3 rounded-lg hover:bg-gray-50 transition-all duration-300"
                                  >
                                    <div className="w-32 h-24 flex-shrink-0 overflow-hidden rounded-lg">
                                      <img
                                        src={
                                          news.featuredImage ||
                                          news.image ||
                                          "/placeholder-news.jpg"
                                        }
                                        alt={news.title}
                                        className="w-full h-full object-cover object-top group-hover/news:scale-110 transition-transform duration-300"
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs text-[#00c897] font-semibold mb-1">
                                        {news.category?.name ||
                                          news.categoryName ||
                                          ""}
                                      </div>
                                      <h4 className="font-bold text-[#333] group-hover/news:text-[#00c897] transition-colors text-sm mb-2 line-clamp-2">
                                        {news.title}
                                      </h4>
                                      <p className="text-xs text-gray-600 line-clamp-2">
                                        {news.excerpt || news.description || ""}
                                      </p>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <Link
              to="/lien-he"
              className={`hidden lg:flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap text-sm ${
                useDarkText
                  ? "bg-[#00c897] text-white hover:bg-[#00b587]"
                  : "bg-white text-[#333] hover:bg-gray-100"
              }`}
            >
              <i className="ri-phone-line"></i>
              Tư Vấn Miễn Phí
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                useDarkText
                  ? "text-[#333] hover:bg-gray-100"
                  : "text-white hover:bg-white/10"
              }`}
            >
              <i
                className={`text-2xl ${
                  mobileMenuOpen ? "ri-close-line" : "ri-menu-line"
                }`}
              ></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          <div className="absolute top-20 left-0 right-0 bg-white shadow-2xl max-h-[calc(100vh-5rem)] overflow-y-auto">
            <nav className="p-6 space-y-2">
              {navLinks.map((link) => (
                <div key={link.path}>
                  {link.hasMegaMenu ? (
                    <>
                      <div className={`flex items-center px-6 py-4 rounded-lg ${
                        location.pathname === link.path
                          ? "bg-[#00c897] text-white"
                          : "text-[#333] hover:bg-gray-100"
                      }`}>
                        <Link
                          to={link.path}
                          className="flex-1 font-semibold transition-colors"
                        >
                          {link.label}
                        </Link>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setMobileDropdownOpen(
                              mobileDropdownOpen === link.label ? null : link.label
                            );
                          }}
                          className="ml-2 p-1 hover:bg-white/20 rounded transition-colors"
                        >
                          <i className={`ri-arrow-${mobileDropdownOpen === link.label ? 'up' : 'down'}-s-line text-xl`}></i>
                        </button>
                      </div>
                      {link.hasMegaMenu && link.megaMenuType === "solutions" && mobileDropdownOpen === link.label && (
                        <div className="mt-2 ml-4 space-y-4">
                          {/* Solutions */}
                          {layoutData.solutions.length > 0 && (
                            <div className="pl-4 border-l-2 border-[#2a4b7c]">
                              <div className="text-xs font-bold text-[#2a4b7c] uppercase tracking-wider mb-3">
                                Giải Pháp
                              </div>
                              <div className="space-y-2">
                                {layoutData.solutions.map((solution) => (
                                  <Link
                                    key={solution._id}
                                    to={`/giai-phap/${solution.slug}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="block py-2 px-3 rounded-lg text-sm text-[#333] hover:bg-gray-100 hover:text-[#00c897] transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      <i className={`${solution.icon || 'ri-service-line'} text-[#2a4b7c]`}></i>
                                      <span className="font-semibold">{solution.name}</span>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Services */}
                          {layoutData.services.length > 0 && (
                            <div className="pl-4 border-l-2 border-[#00c897]">
                              <div className="text-xs font-bold text-[#00c897] uppercase tracking-wider mb-3">
                                Dịch Vụ
                              </div>
                              <div className="space-y-2">
                                {layoutData.services.map((service) => (
                                  <Link
                                    key={service._id}
                                    to={`/dich-vu/${service.slug}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="block py-2 px-3 rounded-lg text-sm text-[#333] hover:bg-gray-100 hover:text-[#00c897] transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      <i className={`${service.icon || 'ri-service-line'} text-[#00c897]`}></i>
                                      <span className="font-semibold">{service.name}</span>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Academy */}
                          {layoutData.academy && (
                            <div className="pl-4 border-l-2 border-[#00c897]">
                              <div className="text-xs font-bold text-[#00c897] uppercase tracking-wider mb-3">
                                Academy
                              </div>
                              <Link
                                to={`/academy/${layoutData.academy.slug}`}
                                onClick={() => setMobileMenuOpen(false)}
                                className="block py-2 px-3 rounded-lg text-sm text-[#333] hover:bg-gray-100 hover:text-[#00c897] transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <i className={`${layoutData.academy.icon || 'ri-graduation-cap-line'} text-[#00c897]`}></i>
                                  <span className="font-semibold">{layoutData.academy.name}</span>
                                </div>
                              </Link>
                            </div>
                          )}
                        </div>
                      )}
                      {link.hasMegaMenu && link.megaMenuType === "news" && mobileDropdownOpen === link.label && (
                        <div className="mt-2 ml-4 space-y-2">
                          {layoutData.newsCategories.map((category: any) => (
                            <Link
                              key={category._id}
                              to={`/tin-tuc-${category.slug}`}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block py-3 px-4 rounded-lg text-sm text-[#333] hover:bg-gray-100 hover:text-[#00c897] transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <i
                                  className={`${category.icon || "ri-folder-line"}`}
                                ></i>
                                <span className="font-semibold">
                                  {category.name}
                                </span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      className={`block px-6 py-4 rounded-lg font-semibold transition-colors ${
                        location.pathname === link.path
                          ? "bg-[#00c897] text-white"
                          : "text-[#333] hover:bg-gray-100"
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              <Link
                to="/lien-he"
                className="block px-6 py-4 bg-[#00c897] text-white text-center font-semibold rounded-lg whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Tư Vấn Miễn Phí
              </Link>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
